"use client";

/**
 * Image carousel preview for canvas renderers
 * Interactive carousel with navigation arrows, dot pagination, and optional click handler
 * Used by VenueRenderer and CarouselRenderer
 */

import type React from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, Image } from "lucide-react";
import { isSafeUrl } from "../../../../_logic/utils";
import type { ComponentImage } from "../../../../_types";
import { VALID_IMAGE_ACCEPT } from "@/lib/utils/media-validation";

/** Fallback aspect ratio (16:9) used while the first image is loading — mirrors the public ImageCarousel */
const FALLBACK_ASPECT_RATIO = 16 / 9;
/** Maximum number of dot indicators to render before collapsing into a "+N" count */
const MAX_DOTS = 5;

interface ImageCarouselPreviewProps {
  /** Array of images to display */
  images?: ComponentImage[];
  /** Called when an image is clicked (e.g., to open edit modal) */
  onImageClick?: (image: ComponentImage, index: number) => void;
  /** Called when files are selected from the empty state file picker */
  onFilesSelected?: (files: File[]) => void;
}

/**
 * Renders an interactive carousel preview with navigation arrows and dot indicators
 */
export function ImageCarouselPreview({
  images,
  onImageClick,
  onFilesSelected,
}: ImageCarouselPreviewProps): React.ReactElement {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [aspectRatio, setAspectRatio] = useState(FALLBACK_ASPECT_RATIO);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // Detect aspect ratio from the first image so the canvas preview matches the
  // public carousel's natural-ratio sizing (mirrors public ImageCarousel).
  useEffect(() => {
    const first = images?.[0];
    if (!first || !isSafeUrl(first.url)) return;
    const img = new window.Image();
    img.onload = () => {
      if (img.naturalWidth > 0 && img.naturalHeight > 0) {
        setAspectRatio(img.naturalWidth / img.naturalHeight);
      }
    };
    img.src = first.url;
  }, [images]);

  const handlePrev = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      if (!images) return;
      setCurrentIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
    },
    [images]
  );

  const handleNext = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      if (!images) return;
      setCurrentIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));
    },
    [images]
  );

  const handleEmptyClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files;
      if (files && files.length > 0 && onFilesSelected) {
        onFilesSelected(Array.from(files));
      }
      // Reset so the same file can be re-selected
      e.target.value = "";
    },
    [onFilesSelected]
  );

  if (!images || images.length === 0) {
    return (
      <>
        {onFilesSelected && (
          <input
            ref={fileInputRef}
            type="file"
            accept={VALID_IMAGE_ACCEPT}
            multiple
            onChange={handleFileChange}
            className="hidden"
            aria-label="Upload images"
          />
        )}
        <div
          className={`flex aspect-video items-center justify-center rounded-lg bg-gray-100 ${onFilesSelected ? "cursor-pointer transition-colors hover:bg-gray-200" : ""}`}
          onClick={onFilesSelected ? handleEmptyClick : undefined}
          role={onFilesSelected ? "button" : undefined}
          tabIndex={onFilesSelected ? 0 : undefined}
          onKeyDown={
            onFilesSelected
              ? (e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    handleEmptyClick();
                  }
                }
              : undefined
          }
          aria-label={onFilesSelected ? "Click to add images" : undefined}
        >
          <div className="flex flex-col items-center gap-2 text-gray-400">
            <Image className="h-8 w-8" aria-hidden="true" />
            <span className="font-medium">No images added</span>
          </div>
        </div>
      </>
    );
  }

  // Clamp index in case images array shrinks
  const safeIndex = Math.min(currentIndex, images.length - 1);
  const currentImage = images[safeIndex];

  return (
    <div className="group/carousel relative">
      {/* Image container — fixed 4:3 on narrow viewports, natural aspect ratio at md+ (mirrors public ImageCarousel) */}
      <div
        className="relative aspect-[4/3] overflow-hidden rounded-2xl bg-gray-100 md:[aspect-ratio:var(--carousel-aspect)]"
        style={{ "--carousel-aspect": aspectRatio.toString() } as React.CSSProperties}
      >
        <div
          className={onImageClick ? "h-full w-full cursor-pointer" : "h-full w-full"}
          onClick={onImageClick ? () => onImageClick(currentImage, safeIndex) : undefined}
          role={onImageClick ? "button" : undefined}
          tabIndex={onImageClick ? 0 : undefined}
          onKeyDown={
            onImageClick
              ? (e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    onImageClick(currentImage, safeIndex);
                  }
                }
              : undefined
          }
          aria-label={onImageClick ? `Edit image: ${currentImage.name}` : undefined}
        >
          {isSafeUrl(currentImage.url) ? (
            <img src={currentImage.url} alt={currentImage.name} className="h-full w-full object-cover" loading="lazy" />
          ) : (
            <div className="flex h-full w-full items-center justify-center bg-gray-100">
              <span className="text-gray-400">Invalid image URL</span>
            </div>
          )}
        </div>

        {/* Inline dot indicators — Airbnb-style, inset at bottom (mirrors public ImageCarousel) */}
        {images.length > 1 && (
          <div className="absolute bottom-3 left-1/2 -translate-x-1/2">
            <div className="flex items-center gap-1.5 rounded-full bg-black/30 px-2.5 py-1.5 backdrop-blur-sm">
              {images.slice(0, MAX_DOTS).map((img, idx) => (
                <button
                  key={img.id}
                  type="button"
                  className={`block h-1.5 rounded-full bg-white transition-all ${idx === safeIndex ? "w-4 opacity-100" : "w-1.5 opacity-60 hover:opacity-80"}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    setCurrentIndex(idx);
                  }}
                  aria-label={`Go to image ${idx + 1}`}
                />
              ))}
              {images.length > MAX_DOTS && (
                <span className="ml-0.5 text-xs font-medium text-white">+{images.length - MAX_DOTS}</span>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Navigation arrows — hover-revealed, soft outlined white circles (mirrors public ImageCarousel) */}
      {images.length > 1 && (
        <>
          <button
            type="button"
            className="absolute left-3 top-1/2 z-10 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full border border-gray-200 bg-white text-gray-700 shadow-sm transition-all hover:bg-gray-50 hover:shadow-md focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-300 md:opacity-0 md:group-hover/carousel:opacity-100"
            onClick={handlePrev}
            aria-label="Previous image"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <button
            type="button"
            className="absolute right-3 top-1/2 z-10 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full border border-gray-200 bg-white text-gray-700 shadow-sm transition-all hover:bg-gray-50 hover:shadow-md focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-300 md:opacity-0 md:group-hover/carousel:opacity-100"
            onClick={handleNext}
            aria-label="Next image"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </>
      )}
    </div>
  );
}
